import { Field, ConditionalItem } from "../elements";
import { Controller, useWatch } from "react-hook-form";
import { MenuItem, Select } from "@material-ui/core";
import { useState, useMemo } from "react";

export const Conditionals = ({
  field,
  register,
  control: control,
  errors,
  setValue,
  disabled,
}) => {
  const [value, currentValue] = useState(field.value[0]?.key);
  let winX = null;
  let winY = null;

  // Collect all additional condition dependee field names
  const additionalDependees = useMemo(() => {
    const dependees = new Set();
    field.conditional?.forEach((cond) => {
      if (cond.additionalConditions) {
        cond.additionalConditions.forEach((ac) => {
          if (ac.dependee) {
            dependees.add(ac.dependee);
          }
        });
      }
    });
    return Array.from(dependees);
  }, [field.conditional]);

  // Watch all additional dependee field values
  const watchedValues = useWatch({
    control,
    name: additionalDependees,
  });

  // Create a map of dependee name to value
  const dependeeValues = useMemo(() => {
    const values = {};
    additionalDependees.forEach((name, idx) => {
      values[name] = watchedValues?.[idx] ?? watchedValues;
    });
    return values;
  }, [additionalDependees, watchedValues]);

  // Function to check if a conditional field should be visible
  const isVisible = (conditionalConfig) => {
    // Check primary condition (current field value)
    const primaryMatch = conditionalConfig.valuesSet?.split(',').includes(`${value}`);

    // Determine grouping logic from primary or additional conditions
    // Default to OR if not specified
    const grouping = conditionalConfig.grouping ||
      conditionalConfig.additionalConditions?.[0]?.grouping || 'OR';

    // If no additional conditions, just check primary
    if (!conditionalConfig.additionalConditions || conditionalConfig.additionalConditions.length === 0) {
      return primaryMatch;
    }

    // Check additional conditions
    const additionalMatches = conditionalConfig.additionalConditions.map(ac => {
      const dependeeValue = dependeeValues[ac.dependee];
      return dependeeValue && ac.valuesSet?.split(',').includes(`${dependeeValue}`);
    });

    // Apply grouping logic
    if (grouping === 'AND') {
      // AND logic: primary AND all additional conditions must be true
      return primaryMatch && additionalMatches.every(match => match);
    } else {
      // OR logic: primary OR any additional condition must be true
      return primaryMatch || additionalMatches.some(match => match);
    }
  };

  return (
    <>
      <Field
        field={field}
        value={
          <Controller
            render={({ field: { onChange, value, ref } }, ...props) => (
              <div ref={ref} tabIndex="-1" className="outline-none">
                <Select
                  onChange={(e) => {
                    onChange(e.target.value);
                    currentValue(e.target.value);
                  }}
                  value={value}
                  variant="outlined"
                  displayEmpty
                  disabled={disabled}
                  onOpen={() => {
                    winX = window.scrollX;
                    winY = window.scrollY;
                  }}
                  onClose={() => {
                    winX = null;
                    winY = null;
                  }}
                  className="select"
                  MenuProps={{
                    disableScrollLock: true,
                    className: "select-paper",
                    disablePortal: true,
                  }}
                  style={{ backgroundColor: disabled ? "#FAFAFA" : "white" }}
                >
                  <MenuItem value="" disabled>
                    Válassz egy lehetőséget
                  </MenuItem>
                  {field.allowedValues.map(({ key, value }) => (
                    <MenuItem
                      style={{
                        whiteSpace: "unset",
                        wordBreak: "break-word",
                      }}
                      selected={key == field.value}
                      value={key}
                    >
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}
            name={field.name}
            defaultValue={field.value[0]?.key || ""}
            control={control}
          />
        }
      />
      {(() => {
        let renderFields = [];
        let renderData = [];
        field.conditional.forEach((el) => renderFields.push(...el.children));
        renderFields.forEach((el, idx) =>
          renderData.push(
            <ConditionalItem
              field={[el]}
              register={register}
              control={control}
              errors={errors}
              visible={isVisible(field?.conditional?.[idx])}
              setValue={setValue}
              disabled={disabled}
            />
          )
        );
        return renderData;
      })()}
    </>
  );
};
